// S4 Routing editor (spec 01 §4). Ordered step list (drag/keyboard reorder,
// renumber by 10s) · per-step drawer (work-center picker facility-filtered,
// setup/run, run basis, setup-key autocomplete, alternates, operation scrap, QC
// plan, capture/backflush/outsource toggles, instructions rich editor) · total
// lead-time preview / 1000 m · Activate dialog (as S3) · warning chip when a BOM
// consume-at-step references a missing step · version switcher + effectivity
// timeline + diff.
//
// Route already wired (router.tsx: products/$id/routings/$routingId → this
// export). Data flows through the shared catalogue query hooks; work centers come
// from Module 16 via the local useWorkCenters hook. There is NO cost surface on
// this screen (§4 S4), so no cost field is ever read or rendered here.
//
// Permission model: editing is gated on the routing being a `draft` (§3.5 —
// read-only otherwise, "Create new version" CTA). The server additionally
// enforces catalogue.routing.write / .activate; a 403 is surfaced gracefully.
// A future auth/permissions context can pass `canWrite=false` to also hide the
// controls for a read-only role (e.g. qc) that opens a draft — see notes.
//
// SCHEDULED + WITHDRAW (§3.3.5, §9.16, D-261) — the same treatment as the BOM
// editor: an `active` routing whose `effective_from` is ahead of the
// facility's today reads "Scheduled" (header chip, amber banner naming the
// date), the Versions card states which version the §3.4 resolver answers
// for TODAY, and Withdraw (catalogue.routing.activate) opens the shared
// dialog. A routing is always facility-bound, so "today" is that facility's
// calendar (D-089), with the browser's as the documented fallback.
//
// ARCHIVE (§3.3.5 Archive; D-261 amended 2026-09-08) — as the BOM editor: a
// draft is abandoned, a superseded version housekept, through the shared
// ArchiveDialog, gated on catalogue.routing.activate (the API's own gate on
// `POST /routings/:id/archive`); VERSION_STILL_RESUMABLE and
// ROUTING_NOT_ARCHIVABLE are rendered by name. Both dialogs' previews are
// captured when they OPEN, and both results take focus as they land.

import { enumLabel } from "../../../../lib/enum-label";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Card } from "../../../../components/card";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { Banner, toast } from "../../../../components/toast";
import { StatStrip, StatTile } from "../../../../components/stat-strip";
import { StatusChip } from "../../../../components/status-chip";
import { CardSkeleton, EmptyState, ErrorState } from "../../../../components/states";
import { StatusBadge } from "../../../../components/status-badge";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf, RequestError } from "../../../../lib/request";
import { EffectivityTimeline } from "../components/effectivity-timeline";
import { EffectiveTodayNote, effectiveTodayStateOf } from "../components/effective-today";
import { DiffView, VersionSwitcher, type DiffRow } from "../components/version-switcher";
import {
  archivePreview,
  isScheduledVersion,
  restorePreview,
  toRestoreSummary,
  type ArchivePreview,
  type RestoreSummary,
} from "../components/version-schedule";
import { ArchiveDialog } from "../components/archive-dialog";
import { ResultFocus } from "../components/result-focus";
import { WithdrawDialog } from "../components/withdraw-dialog";
import {
  useActivateRouting,
  useArchiveRouting,
  useCreateRoutingVersion,
  useEffectiveBom,
  useEffectiveRouting,
  useFacilities,
  useProduct,
  useProductRoutings,
  useReplaceRoutingSteps,
  useRouting,
  useWithdrawRouting,
} from "../hooks/catalogue-queries";
import { useCataloguePermissions } from "../hooks/permissions";
import { ActivateDialog, type ActivateWarnings } from "./routing-editor/activate-dialog";
import {
  computeLeadTimeMinutes,
  emptyStep,
  invalidStepNumbers,
  isEditableStatus,
  missingConsumeSteps,
  moveItem,
  renumberSteps,
  stepFromServer,
  stepToInput,
  type StepDraft,
  type WorkCenter,
} from "./routing-editor/model";
// Activation floor: deliberately browser-local with server revalidation
// (`EFFECTIVE_DATE_PAST`, §9.2) — the D-089 tinting half, stated on the helper.
// `facilityTodayIso` is the other half: the SCHEDULED question selects data
// (which version is in force) and so takes the facility's calendar.
import { facilityTodayIso, todayIso } from "../../../../lib/business-dates";
import { StepDrawer } from "./routing-editor/step-drawer";
import { StepList } from "./routing-editor/step-list";
import { useWorkCenters } from "./routing-editor/work-centers";
import "../catalogue.css";

interface StepShape {
  step_no: number;
  name: string;
  work_center_id: string;
  setup_minutes: number;
  run_minutes: number;
  run_basis: string;
  operation_scrap_pct: number;
}

function toDiffRows(
  steps: readonly StepShape[],
  wcById: ReadonlyMap<string, WorkCenter>,
): DiffRow[] {
  return steps.map((s) => ({
    key: String(s.step_no),
    label: `#${s.step_no} ${s.name}`,
    fields: {
      workCenter: wcById.get(s.work_center_id)?.code ?? s.work_center_id,
      setup: s.setup_minutes,
      run: s.run_minutes,
      basis: s.run_basis,
      scrapPct: s.operation_scrap_pct,
    },
  }));
}

interface DrawerState {
  mode: "new" | "edit";
  index: number;
  step: StepDraft;
}

/**
 * The route component. Everything below is keyed on the routing id, the way
 * the BOM editor keys `BomEditorInner` on `bomData.id`: the router reuses ONE
 * component instance across `goToVersion` (no `remountDeps`), so without the
 * key every piece of per-version state — the withdraw dialog's open flag, the
 * withdraw mutation and its result banner, the compare selection, the step
 * drawer — survived a switch to another version and was shown against it. A
 * withdrawal's "Version 2 withdrawn. Version 1 is active again." banner sat
 * on top of version 1's own screen after "Open version 1" was pressed.
 */
export function RoutingEditorScreen({ canWrite }: { canWrite?: boolean } = {}) {
  const params = useParams({ strict: false });
  const productId = params.id ?? "";
  const routingId = params.routingId ?? "";
  return (
    <RoutingEditorBody
      key={routingId}
      productId={productId}
      routingId={routingId}
      canWrite={canWrite}
    />
  );
}

function RoutingEditorBody({
  productId,
  routingId,
  canWrite,
}: {
  productId: string;
  routingId: string;
  canWrite?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // §7 authoring gate — the server enforces catalogue.routing.write/.activate;
  // these hide the authoring + activate affordances for a role that can only read
  // the structure (qc, procurement, finance). A `canWrite` prop still overrides
  // (kept for tests); otherwise it derives from the caller's permissions.
  const perms = useCataloguePermissions();
  const canWriteEffective = canWrite ?? perms.has("catalogue.routing.write");
  const canActivatePerm = perms.has("catalogue.routing.activate");

  const routingQuery = useRouting(routingId);
  const routing = routingQuery.data;
  const facilityId = routing?.facility_id ?? "";

  const productQuery = useProduct(productId);
  const routingsQuery = useProductRoutings(
    productId,
    facilityId.length > 0 ? facilityId : undefined,
  );
  const wcQuery = useWorkCenters(facilityId);
  const bomQuery = useEffectiveBom(
    facilityId.length > 0 ? productId : "",
    facilityId.length > 0 ? { facility_id: facilityId } : {},
  );

  const replaceSteps = useReplaceRoutingSteps(routingId);
  const activate = useActivateRouting(routingId, productId);
  const withdrawMut = useWithdrawRouting(routingId, productId);
  const archiveMut = useArchiveRouting(routingId, productId);
  const createVersion = useCreateRoutingVersion(productId);

  // "Today" for the scheduled/in-force question — the routing's own facility
  // calendar (D-089), the browser's until the facilities read answers. Never
  // gates the screen; the server re-validates every withdraw.
  const facilitiesQ = useFacilities();
  const calendarFacility =
    facilitiesQ.data?.items.find((f) => f.id === facilityId) ?? facilitiesQ.data?.items[0];
  const today = facilityTodayIso(Date.now(), calendarFacility?.timezone);
  // Which routing version the §3.4 resolver answers for TODAY (facility
  // mandatory; the hook stays idle until the routing has loaded).
  const effectiveQ = useEffectiveRouting(productId, { facility_id: facilityId, date: today });

  const [steps, setSteps] = useState<StepDraft[]>([]);
  const [dirty, setDirty] = useState(false);
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [activateOpen, setActivateOpen] = useState(false);
  /* Open while this holds the restore preview CAPTURED at open — never the
   * live recomputation, which a mid-dialog refetch could flip under the
   * planner's decision (the BOM editor's note says why). */
  const [withdrawDialog, setWithdrawDialog] = useState<RestoreSummary | null>(null);
  /* The archive dialog's captured meaning; also what keeps it mounted through
   * a NOT_ARCHIVABLE refusal's refetch. */
  const [archiveDialog, setArchiveDialog] = useState<ArchivePreview | null>(null);
  /* The withdraw RESULT is the mutation's OWN data — the same shape as the
   * BOM editor, for the same reason: a per-call `onSuccess` is dropped when
   * the observer resets mid-flight, and the dialog used to let Escape do
   * exactly that. Dismissing the banner is `reset()`. */
  const withdrawn =
    withdrawMut.data === undefined
      ? null
      : {
          withdrawnVersion: withdrawMut.data.routing.version,
          restoredVersionId: withdrawMut.data.restored_version_id,
        };
  /* The archive RESULT — the mutation's own data, for the same reason. */
  const archived = archiveMut.data === undefined ? null : { version: archiveMut.data.version };
  const [saveError, setSaveError] = useState<string | null>(null);
  const [compareId, setCompareId] = useState("");
  const loadedKeyRef = useRef<string | null>(null);

  // Reload the working steps whenever the server routing changes (first load,
  // version switch, or a successful save's refetch). Unsaved local edits keep
  // the same server key, so a background refetch never clobbers them.
  useEffect(() => {
    if (routing === undefined) return;
    const loadKey = `${routing.id}:${routing.updated_at}`;
    if (loadedKeyRef.current === loadKey) return;
    loadedKeyRef.current = loadKey;
    setSteps(routing.steps.map(stepFromServer));
    setDirty(false);
    setSaveError(null);
  }, [routing]);

  // 401 on any read → sign-in (matches the P0 products screen).
  useEffect(() => {
    const errs = [routingQuery.error, productQuery.error, routingsQuery.error, wcQuery.error];
    if (errs.some((e) => e instanceof UnauthorizedError)) void navigate({ to: "/login" });
  }, [routingQuery.error, productQuery.error, routingsQuery.error, wcQuery.error, navigate]);

  const compareRouting = useRouting(compareId);

  const workCenters: readonly WorkCenter[] = wcQuery.data?.items ?? [];
  const workCenterById = useMemo(() => {
    const map = new Map<string, WorkCenter>();
    for (const wc of workCenters) map.set(wc.id, wc);
    return map;
  }, [workCenters]);

  const stepNos = useMemo(() => new Set(steps.map((s) => s.step_no)), [steps]);
  const missing = useMemo(
    () => (bomQuery.data !== undefined ? missingConsumeSteps(bomQuery.data.lines, stepNos) : []),
    [bomQuery.data, stepNos],
  );
  const leadMinutes = useMemo(() => computeLeadTimeMinutes(steps), [steps]);
  const invalidNos = useMemo(() => invalidStepNumbers(steps), [steps]);

  if (productId.length === 0 || routingId.length === 0) {
    return (
      <ErrorState
        title={t("catalogue.routing.errorTitle")}
        detail={t("catalogue.routing.badRoute")}
        retryLabel={t("catalogue.state.retry")}
        onRetry={() => void navigate({ to: "/catalogue/products" })}
      />
    );
  }

  if (routingQuery.isPending) {
    return <CardSkeleton lines={6} label={t("catalogue.routing.loading")} />;
  }

  if (routingQuery.error instanceof UnauthorizedError) {
    return <CardSkeleton lines={6} label={t("catalogue.routing.loading")} />;
  }

  if (routingQuery.error || routing === undefined) {
    return (
      <ErrorState
        title={t("catalogue.routing.errorTitle")}
        detail={routingQuery.error instanceof Error ? routingQuery.error.message : undefined}
        requestId={requestIdOf(routingQuery.error)}
        requestIdLabel={t("catalogue.state.requestId")}
        retryLabel={t("catalogue.state.retry")}
        onRetry={() => void routingQuery.refetch()}
      />
    );
  }

  const editable = isEditableStatus(routing.status) && canWriteEffective;
  const product = productQuery.data;
  const productLabel =
    product !== undefined ? `${product.sku} · ${product.name}` : routing.product_id;

  const versionOptions = (routingsQuery.data?.items ?? []).map((r) => ({
    id: r.id,
    version: r.version,
    status: r.status,
    effective_from: r.effective_from,
    effective_to: r.effective_to,
  }));
  const activeVersion = versionOptions.find((v) => v.status === "active");
  // Active but not yet in force (§9.16, D-261).
  const scheduled = isScheduledVersion(routing.status, routing.effective_from, today);
  const scheduledFrom = routing.effective_from ?? today;
  const withdrawRestore = toRestoreSummary(
    restorePreview(routingsQuery.data?.items ?? [], {
      id: routing.id,
      effective_from: routing.effective_from,
      facility_id: routing.facility_id,
    }),
  );
  const restoredVersionId = withdrawn?.restoredVersionId ?? null;
  const restoredVersion =
    restoredVersionId === null ? undefined : versionOptions.find((v) => v.id === restoredVersionId);
  /* What archiving THIS routing would mean (§3.3.5), null when not offered.
   * Gated on catalogue.routing.activate — the API's own gate. */
  const archivePlan =
    archived === null ? archivePreview(routing.status, routing.effective_to, today) : null;
  const canArchive = archivePlan !== null && canActivatePerm;

  function openNewStep(): void {
    const last = steps.length > 0 ? steps[steps.length - 1] : undefined;
    setDrawer({ mode: "new", index: steps.length, step: emptyStep(last?.step_no ?? 0) });
  }
  function openEditStep(index: number): void {
    const step = steps[index];
    if (step === undefined) return;
    setDrawer({ mode: "edit", index, step });
  }
  function applyDrawer(next: StepDraft): void {
    setSteps((current) => {
      const draft = drawer;
      if (draft === null) return current;
      const merged =
        draft.mode === "new"
          ? [...current, next]
          : current.map((s, i) => (i === draft.index ? next : s));
      return renumberSteps(merged);
    });
    setDirty(true);
    setSaveError(null);
    setDrawer(null);
  }
  function removeStep(index: number): void {
    setSteps((current) => renumberSteps(current.filter((_, i) => i !== index)));
    setDirty(true);
    setDrawer(null);
  }
  function moveStep(index: number, dir: -1 | 1): void {
    setSteps((current) => renumberSteps(moveItem(current, index, index + dir)));
    setDirty(true);
  }
  function reorderStep(from: number, to: number): void {
    setSteps((current) => renumberSteps(moveItem(current, from, to)));
    setDirty(true);
  }

  function handleSave(): void {
    setSaveError(null);
    if (invalidNos.length > 0) {
      setSaveError(t("catalogue.routing.save.fixSteps", { steps: invalidNos.join(", ") }));
      return;
    }
    replaceSteps.mutate(
      { steps: steps.map(stepToInput) },
      {
        onError: (err: unknown) => {
          if (err instanceof UnauthorizedError) {
            void navigate({ to: "/login" });
            return;
          }
          setSaveError(saveErrorMessage(err, t("catalogue.routing.save.error")));
        },
      },
    );
  }

  function handleActivate(input: { effective_from: string; activation_note: string }): void {
    activate.mutate(input, {
      onSuccess: () => {
        setActivateOpen(false);
      },
      onError: (err: unknown) => {
        if (err instanceof UnauthorizedError) void navigate({ to: "/login" });
      },
    });
  }

  function handleWithdraw(reason: string): void {
    // No success callback: the result and the dialog's closing derive from
    // the mutation's data (`withdrawn` above).
    withdrawMut.mutate(
      { reason },
      {
        onError: (err: unknown) => {
          if (err instanceof UnauthorizedError) void navigate({ to: "/login" });
        },
      },
    );
  }

  function handleArchive(): void {
    archiveMut.mutate(undefined, {
      onError: (err: unknown) => {
        if (err instanceof UnauthorizedError) void navigate({ to: "/login" });
      },
    });
  }

  function handleCreateVersion(): void {
    if (routing === undefined) return;
    createVersion.mutate(
      { facility_id: facilityId, copy_from_version_id: routing.id },
      {
        onSuccess: (created) => {
          // §3.8 — same silent-write class as the BOM editor's version copy.
          toast.success(
            t("catalogue.routing.versionCreated", {
              version: created.version,
              from: routing.version,
            }),
          );
          void navigate({
            to: "/catalogue/products/$id/routings/$routingId",
            params: { id: productId, routingId: created.id },
          });
        },
        onError: (err: unknown) => {
          if (err instanceof UnauthorizedError) void navigate({ to: "/login" });
        },
      },
    );
  }

  function goToVersion(id: string): void {
    if (id === routingId) return;
    void navigate({
      to: "/catalogue/products/$id/routings/$routingId",
      params: { id: productId, routingId: id },
    });
  }

  const activateWarnings: ActivateWarnings = {
    noSteps: steps.length === 0,
    missingConsumeSteps: missing,
    activeVersion:
      activeVersion !== undefined && activeVersion.id !== routing.id ? activeVersion.version : null,
    unsaved: dirty,
  };

  const roundedMinutes = Math.round(leadMinutes);
  const hours = (leadMinutes / 60).toFixed(1);

  return (
    <section className="rte cat-screen" data-testid="routing-editor">
      <PageHeader
        title={`${t("catalogue.routing.title")} v${String(routing.version)}`}
        crumbs={[
          { label: t("nav.catalogue"), to: "/catalogue/products" },
          { label: t("catalogue.productsList.title"), to: "/catalogue/products" },
          { label: productLabel, to: "/catalogue/products" },
        ]}
        actions={
          <>
            {scheduled ? (
              /* Wire status `active`, human meaning "not yet" — one chip. */
              <StatusChip variant="warn" data-status="scheduled" data-testid="version-scheduled">
                {t("catalogue.status.scheduled")}
              </StatusChip>
            ) : (
              <StatusBadge status={routing.status} />
            )}
            {dirty ? (
              <StatusChip variant="warn" data-testid="dirty-flag">
                {t("catalogue.routing.unsaved")}
              </StatusChip>
            ) : (
              <span className="rte-muted">{t("catalogue.routing.allSaved")}</span>
            )}
            {editable ? (
              <>
                <Button onClick={openNewStep}>{t("catalogue.routing.addStep")}</Button>
                {/* Abandon the draft (§3.3.5) — gated as the API gates it. */}
                {canArchive ? (
                  <Button
                    variant="danger"
                    data-testid="archive-open"
                    disabled={archiveMut.isPending}
                    onClick={() => {
                      setArchiveDialog(archivePlan);
                    }}
                  >
                    {t("catalogue.archive.button")}
                  </Button>
                ) : null}
                {/* Activation is a distinct §7 code (catalogue.routing.activate). */}
                {canActivatePerm ? (
                  <Button
                    disabled={dirty || steps.length === 0}
                    onClick={() => {
                      setActivateOpen(true);
                    }}
                  >
                    {t("catalogue.routing.activate.open")}
                  </Button>
                ) : null}
                <Button
                  variant="primary"
                  disabled={!dirty}
                  busy={replaceSteps.isPending}
                  busyLabel={t("catalogue.routing.save.saving")}
                  onClick={handleSave}
                >
                  {t("catalogue.routing.save.save")}
                </Button>
              </>
            ) : (
              <>
                {/* Housekeeping (a superseded routing) — the same §7 code. */}
                {canArchive ? (
                  <Button
                    variant="danger"
                    data-testid="archive-open"
                    disabled={archiveMut.isPending}
                    onClick={() => {
                      setArchiveDialog(archivePlan);
                    }}
                  >
                    {t("catalogue.archive.button")}
                  </Button>
                ) : null}
                {canWriteEffective ? (
                  <Button variant="primary" onClick={handleCreateVersion}>
                    {t("catalogue.routing.createNewVersion")}
                  </Button>
                ) : null}
              </>
            )}
          </>
        }
      />

      {scheduled ? (
        /* Amber (§1 rule 3): active-but-not-in-force is the one read-only
         * state a planner must not mistake for business as usual. Withdraw is
         * gated on the SAME §7 code as activate. */
        <Banner
          kind="warn"
          data-testid="routing-scheduled-banner"
          aside={
            canActivatePerm ? (
              <Button
                variant="danger"
                data-testid="withdraw-open"
                disabled={withdrawMut.isPending}
                onClick={() => {
                  setWithdrawDialog(withdrawRestore);
                }}
              >
                {t("catalogue.withdraw.button")}
              </Button>
            ) : undefined
          }
        >
          {t("catalogue.routing.scheduledBanner", { date: scheduledFrom })}
        </Banner>
      ) : !isEditableStatus(routing.status) ? (
        <Banner kind="neutral" data-testid="readonly-banner">
          {t("catalogue.routing.readOnlyBanner", {
            status: enumLabel(t, "catalogue.status", routing.status),
          })}
        </Banner>
      ) : null}

      {/* The withdraw RESULT (§3.8: inline is the record). A null
       * `restored_version_id` is the legal no-active-routing state — amber,
       * because the next work order will not find one. It takes focus as it
       * lands: the dialog's opener is about to vanish with the refetch. */}
      {withdrawn !== null ? (
        <ResultFocus testId="routing-withdrawn-focus">
          <Banner
            kind={withdrawn.restoredVersionId === null ? "warn" : "ok"}
            data-testid="routing-withdrawn"
            data-restored={withdrawn.restoredVersionId === null ? "none" : "version"}
            dismissLabel={t("catalogue.state.dismiss")}
            onDismiss={() => {
              setWithdrawDialog(null);
              withdrawMut.reset();
            }}
            aside={
              restoredVersion !== undefined ? (
                <Button
                  size="sm"
                  onClick={() => {
                    goToVersion(restoredVersion.id);
                  }}
                >
                  {t("catalogue.withdraw.openRestored", { version: restoredVersion.version })}
                </Button>
              ) : undefined
            }
          >
            {withdrawn.restoredVersionId === null
              ? t("catalogue.withdraw.resultNone", { withdrawn: withdrawn.withdrawnVersion })
              : restoredVersion !== undefined
                ? t("catalogue.withdraw.resultRestored", {
                    withdrawn: withdrawn.withdrawnVersion,
                    restored: restoredVersion.version,
                  })
                : // Not in the list on screen: name the id, never a blank.
                  t("catalogue.withdraw.resultRestoredById", {
                    withdrawn: withdrawn.withdrawnVersion,
                    id: withdrawn.restoredVersionId,
                  })}
          </Banner>
        </ResultFocus>
      ) : null}

      {/* The archive RESULT — the same inline record, the same focus move. */}
      {archived !== null ? (
        <ResultFocus testId="routing-archived-focus">
          <Banner
            kind="ok"
            data-testid="routing-archived"
            dismissLabel={t("catalogue.state.dismiss")}
            onDismiss={() => {
              setArchiveDialog(null);
              archiveMut.reset();
            }}
          >
            {t("catalogue.archive.result", { version: archived.version })}
          </Banner>
        </ResultFocus>
      ) : null}

      {missing.length > 0 ? (
        <Banner kind="warn" data-testid="consume-warning">
          {t("catalogue.routing.consumeWarning", { steps: missing.join(", ") })}
        </Banner>
      ) : null}

      {saveError !== null ? (
        <Banner kind="bad" data-testid="save-error">
          {saveError}
          {requestIdOf(replaceSteps.error) !== undefined ? (
            <span className="rte-request-id">
              {" "}
              {t("catalogue.state.requestId")}: <code>{requestIdOf(replaceSteps.error)}</code>
            </span>
          ) : null}
        </Banner>
      ) : null}

      <StatStrip>
        <StatTile
          label={t("catalogue.routing.leadTime.label")}
          value={t("catalogue.routing.leadTime.value", { minutes: roundedMinutes, hours })}
          sub={t("catalogue.routing.leadTime.sub", { count: steps.length })}
          data-testid="lead-time"
        />
      </StatStrip>

      <Card title={t("catalogue.routing.stepsTitle")} count={steps.length}>
        {steps.length === 0 ? (
          <EmptyState
            title={t("catalogue.routing.emptyTitle")}
            body={t("catalogue.routing.emptyBody")}
            ctaLabel={editable ? t("catalogue.routing.emptyCta") : undefined}
            onCta={editable ? openNewStep : undefined}
          />
        ) : (
          <StepList
            steps={steps}
            workCenterById={workCenterById}
            readOnly={!editable}
            onEdit={openEditStep}
            onMove={moveStep}
            onRemove={removeStep}
            onReorder={reorderStep}
          />
        )}
      </Card>

      <Card title={t("catalogue.routing.versionsTitle")} count={versionOptions.length}>
        <div className="rte-versions bom-col-body">
          <EffectiveTodayNote
            today={today}
            state={effectiveTodayStateOf(effectiveQ)}
            currentVersionId={routing.id}
            onOpen={goToVersion}
            onRetry={() => void effectiveQ.refetch()}
          />
          <EffectivityTimeline
            versions={versionOptions}
            selectedId={routing.id}
            onSelect={goToVersion}
            today={today}
          />
          {versionOptions.length > 1 ? (
            <div className="rte-compare">
              <VersionSwitcher
                versions={versionOptions.filter((v) => v.id !== routing.id)}
                value={compareId}
                onChange={setCompareId}
                label={t("catalogue.routing.compareWith")}
                today={today}
              />
              {compareId.length > 0 && compareRouting.data !== undefined ? (
                <DiffView
                  before={toDiffRows(compareRouting.data.steps, workCenterById)}
                  after={toDiffRows(steps, workCenterById)}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>

      {drawer !== null ? (
        <StepDrawer
          step={drawer.step}
          mode={drawer.mode}
          workCenters={workCenters}
          wcLoading={wcQuery.isPending && facilityId.length > 0}
          wcError={wcQuery.error !== null && !(wcQuery.error instanceof UnauthorizedError)}
          setupKeyPool={steps}
          readOnly={!editable}
          onApply={applyDrawer}
          onCancel={() => {
            setDrawer(null);
          }}
          onDelete={
            drawer.mode === "edit" && editable
              ? () => {
                  removeStep(drawer.index);
                }
              : undefined
          }
        />
      ) : null}

      {activateOpen ? (
        <ActivateDialog
          todayIso={todayIso()}
          warnings={activateWarnings}
          pending={activate.isPending}
          errorMessage={
            activate.error instanceof Error && !(activate.error instanceof UnauthorizedError)
              ? activate.error.message
              : null
          }
          requestId={requestIdOf(activate.error)}
          onConfirm={handleActivate}
          onCancel={() => {
            setActivateOpen(false);
          }}
        />
      ) : null}

      {withdrawDialog !== null && withdrawn === null ? (
        <WithdrawDialog
          version={routing.version}
          effectiveFrom={scheduledFrom}
          restore={withdrawDialog}
          pending={withdrawMut.isPending}
          error={withdrawMut.error instanceof UnauthorizedError ? null : withdrawMut.error}
          onConfirm={handleWithdraw}
          onCancel={() => {
            setWithdrawDialog(null);
            withdrawMut.reset();
          }}
          testId="routing-withdraw-dialog"
        />
      ) : null}

      {archiveDialog !== null && archived === null ? (
        <ArchiveDialog
          version={routing.version}
          preview={archiveDialog}
          effectiveTo={routing.effective_to}
          pending={archiveMut.isPending}
          error={archiveMut.error instanceof UnauthorizedError ? null : archiveMut.error}
          onConfirm={handleArchive}
          onCancel={() => {
            setArchiveDialog(null);
            archiveMut.reset();
          }}
          testId="routing-archive-dialog"
        />
      ) : null}
    </section>
  );
}

function saveErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof RequestError) {
    if (err.fieldErrors.length > 0) {
      const paths = err.fieldErrors.map((fe) => fe.path).join(", ");
      return `${err.message} (${paths})`;
    }
    return err.message;
  }
  return err instanceof Error ? err.message : fallback;
}
